import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconChevronRight,
  IconClipboardList,
  IconInfoCircle,
  IconRobot,
  IconShieldCheck,
  IconShieldExclamation,
  IconShoppingCart,
} from "@tabler/icons-react";
import { useState } from "react";
import {
  KYC,
  KycAiFieldFinding,
  KycAiLinkedProduct,
  KycAiPepAssessment,
  KycAiPepCategory,
  KycAiPrecheck,
  KycAiPrecheckLabel,
  PoliticallyExposedType,
} from "../../types";

// A small, clear qualitative scale so a reviewer immediately knows how much
// scrutiny the case needs. Driven by the model label + findings count only.
const assessmentFor = (
  label: KycAiPrecheckLabel | undefined,
  findingsCount: number,
): { text: string; color: string } => {
  if (label === "incomplete") {
    return { text: "Missing information — needs completion", color: "red" };
  }
  if (label === "review") {
    if (findingsCount >= 3) {
      return { text: "Highly suspicious — review carefully", color: "red" };
    }
    return { text: "Flagged — needs a closer look", color: "orange" };
  }
  // good
  if (findingsCount > 0) {
    return { text: "Mostly fine — a few items to verify", color: "yellow" };
  }
  return { text: "All clear — nothing concerning found", color: "green" };
};

// Humanize a KYC field key ("employment_occupation" -> "Employment occupation")
// for display, while keeping the raw key available on hover.
const humanizeField = (field: string): string => {
  const spaced = field
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const severityMeta = (severity: KycAiFieldFinding["severity"]) =>
  severity === "warning"
    ? { color: "red", label: "Warning" }
    : { color: "orange", label: "Attention" };

const PEP_CATEGORY_LABELS: Record<KycAiPepCategory, string> = {
  headsOfStatesGovernments: "Head of State / Government",
  seniorPoliticians: "Senior Politician",
  seniorGovernmentalOfficials: "Senior Government Official",
  seniorJudicialOfficials: "Senior Judicial Official",
  seniorMilitaryOfficials: "Senior Military Official",
  seniorDiplomats: "Senior Diplomat",
  seniorExecOfStateOwnedCompanies: "State-Owned Company Executive",
  politicalPartyOfficials: "Political Party Head",
  none: "No match",
};

// Always-visible PEP likelihood panel. Display only — no actions, no effect on
// the review workflow. The declared-vs-AI comparison is computed here in the
// frontend; the model is never shown the customer's PEP declaration details.
const PepSection = ({ pep, kycData }: { pep: KycAiPepAssessment; kycData?: KYC }) => {
  const likelihood = Math.min(100, Math.max(0, pep.likelihood ?? 0));
  const color = likelihood >= 70 ? "red" : likelihood >= 40 ? "yellow" : "green";
  const declaredPep =
    kycData?.politicallyExposedType === PoliticallyExposedType.Me ||
    kycData?.politicallyExposed === true;

  // One short status line; mismatches get the highlighted strip.
  let status: { color: string; emphasized: boolean; text: string };
  if (likelihood >= 70) {
    status = declaredPep
      ? { color: "green", emphasized: false, text: "Already marked as PEP" }
      : { color: "red", emphasized: true, text: "Not marked as PEP — verify" };
  } else if (likelihood < 40) {
    status = declaredPep
      ? { color: "orange", emphasized: true, text: "Marked as PEP, but no indicators found" }
      : { color: "gray", emphasized: false, text: "None declared — consistent" };
  } else {
    status = declaredPep
      ? { color: "yellow", emphasized: false, text: "Already marked as PEP" }
      : { color: "yellow", emphasized: false, text: "Possible indicators — not marked as PEP" };
  }

  return (
    <Paper withBorder radius="md" p="xs" mt={8} style={{ borderLeft: `3px solid var(--mantine-color-${color}-6)` }}>
      <Group justify="space-between" wrap="nowrap">
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
          <ThemeIcon color={color} variant="light" size="sm" radius="xl">
            {likelihood >= 70 ? <IconShieldExclamation size={13} /> : <IconShieldCheck size={13} />}
          </ThemeIcon>
          <Text fw={600} size="sm" style={{ whiteSpace: "nowrap" }}>PEP</Text>
          {pep.matchedCategory !== "none" && (
            <Tooltip label={pep.reasoning || pep.matchedCategory} withArrow multiline maw={280} openDelay={300}>
              <Badge size="sm" variant="light" color={color} style={{ textTransform: "none" }}>
                {PEP_CATEGORY_LABELS[pep.matchedCategory] ?? pep.matchedCategory}
              </Badge>
            </Tooltip>
          )}
        </Group>
        <Badge size="lg" variant="filled" color={color} style={{ flexShrink: 0 }}>{likelihood}%</Badge>
      </Group>
      {status.emphasized ? (
        <Paper mt={6} px={8} py={4} radius="sm" bg={`var(--mantine-color-${status.color}-light)`}>
          <Group gap={6} wrap="nowrap">
            <IconAlertTriangle size={13} color={`var(--mantine-color-${status.color}-7)`} style={{ flexShrink: 0 }} />
            <Text size="xs" fw={600} c={`${status.color}.9`}>{status.text}</Text>
          </Group>
        </Paper>
      ) : (
        <Group gap={6} mt={6} wrap="nowrap">
          <IconCheck size={12} color={`var(--mantine-color-${status.color === "gray" ? "gray" : status.color}-6)`} style={{ flexShrink: 0 }} />
          <Text size="xs" c="dimmed">{status.text}</Text>
        </Group>
      )}
    </Paper>
  );
};

const productKind = (p: KycAiLinkedProduct): string => {
  const parts = [p.productType === "order" ? "Order" : "Service"];
  if (p.kind) parts.push(p.kind);
  return parts.join(" · ");
};

const FieldFindingRow = ({ finding }: { finding: KycAiFieldFinding }) => {
  const meta = severityMeta(finding.severity);
  return (
    <Paper withBorder radius="md" p="sm" style={{ borderLeft: `3px solid var(--mantine-color-${meta.color}-6)` }}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
          <ThemeIcon color={meta.color} variant="light" size="sm" radius="xl">
            <IconAlertTriangle size={13} />
          </ThemeIcon>
          <Tooltip label={finding.field} openDelay={400} withArrow>
            <Text fw={600} size="sm" style={{ whiteSpace: "nowrap" }}>{humanizeField(finding.field)}</Text>
          </Tooltip>
        </Group>
        <Badge size="xs" color={meta.color} variant="light">{meta.label}</Badge>
      </Group>
      <Text size="sm" mt={6} c="dark">{finding.note}</Text>
    </Paper>
  );
};

// Compact KYC AI review summary (private/ai_precheck). Advisory only. The main
// card stays short (bot icon, clear assessment, findings count, short summary,
// centred details button); the specifics open in a rich modal. To avoid
// duplicated content we show the short summary, per-field findings and key
// reasons — nothing that just restates another section.
export const AiPrecheckCard = ({ precheck, kycData }: { precheck: KycAiPrecheck; kycData?: KYC }) => {
  const [open, setOpen] = useState(false);
  const isError = precheck.status === "error";
  const isBudgetSkipped = precheck.status === "budget_skipped";
  const isCompleted = precheck.status === "completed";

  const findings = precheck.fieldFindings ?? [];
  const reasons = precheck.reasons ?? [];
  const missing = precheck.missingFields ?? [];
  const products = precheck.linkedProducts ?? [];
  const hasDetails = isCompleted && (
    reasons.length > 0 || findings.length > 0 || missing.length > 0 || products.length > 0
  );

  const assessment = assessmentFor(precheck.label, findings.length);
  const headerColor = isError ? "red" : isCompleted ? assessment.color : "gray";

  return (
    <Paper shadow="xs" p="sm" withBorder>
      {/* Title row — only short count badges live here so nothing gets cropped. */}
      <Group justify="space-between" wrap="nowrap" align="center">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <ThemeIcon color={headerColor} variant="light" size="md" radius="xl">
            <IconRobot size={18} />
          </ThemeIcon>
          <Text fw={600} size="sm">AI KYC review</Text>
        </Group>
        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          {isCompleted && findings.length > 0 && (
            <Tooltip label={`${findings.length} field${findings.length === 1 ? "" : "s"} to check`} withArrow>
              <Badge size="sm" variant="filled" color="orange" leftSection={<IconClipboardList size={11} />}>
                {findings.length}
              </Badge>
            </Tooltip>
          )}
          {isCompleted && products.length > 0 && (
            <Tooltip label={`${products.length} linked order${products.length === 1 ? "" : "s"} / service${products.length === 1 ? "" : "s"}`} withArrow>
              <Badge size="sm" variant="light" color="blue" leftSection={<IconShoppingCart size={11} />}>
                {products.length}
              </Badge>
            </Tooltip>
          )}
          {isBudgetSkipped && <Badge size="sm" variant="light" color="gray">Budget skipped</Badge>}
          {isError && <Badge size="sm" variant="light" color="red">Error</Badge>}
        </Group>
      </Group>

      {/* Assessment on its own full-width line so the phrase is never truncated. */}
      {isCompleted && (
        <Badge
          mt={8}
          size="sm"
          variant="light"
          color={assessment.color}
          leftSection={precheck.label === "good" && findings.length === 0
            ? <IconCheck size={11} />
            : <IconAlertCircle size={11} />}
          style={{ maxWidth: "100%" }}
          styles={{ label: { overflow: "visible", textOverflow: "clip" } }}
        >
          {assessment.text}
        </Badge>
      )}

      {/* PEP likelihood — always visible on the card, never in the modal. */}
      {isCompleted && precheck.pepAssessment && (
        <PepSection pep={precheck.pepAssessment} kycData={kycData} />
      )}

      {isError && (
        <Alert mt="xs" py={6} color="red" variant="light" icon={<IconAlertCircle size={14} />}>
          {precheck.errorMessage || "The AI review could not be completed."}
        </Alert>
      )}
      {isBudgetSkipped && (
        <Alert mt="xs" py={6} color="gray" variant="light" icon={<IconInfoCircle size={14} />}>
          Skipped — shared daily/monthly AI limit reached.
        </Alert>
      )}

      {isCompleted && precheck.summary && (
        <Text size="sm" mt={8} c="dark">{precheck.summary}</Text>
      )}

      {/* Centred details button below the summary. */}
      {hasDetails && (
        <Stack gap={4} mt={12} align="center">
          <Button
            size="compact-sm"
            variant="light"
            color={assessment.color === "green" ? "gray" : assessment.color}
            rightSection={<IconChevronRight size={14} />}
            onClick={() => setOpen(true)}
          >
            View details
          </Button>
        </Stack>
      )}

      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        size="xl"
        radius="md"
        scrollAreaComponent={ScrollArea.Autosize}
        title={
          <Group gap="xs">
            <ThemeIcon color={headerColor} variant="light" size="md" radius="xl">
              <IconRobot size={18} />
            </ThemeIcon>
            <div>
              <Text fw={700}>AI KYC review</Text>
              <Text size="xs" c="dimmed">Advisory triage — does not change KYC workflow state</Text>
            </div>
          </Group>
        }
      >
        <Stack gap="lg">
          {/* Verdict banner — assessment text is enough; no raw label/confidence. */}
          <Paper
            radius="md"
            p="md"
            style={{
              background: `var(--mantine-color-${assessment.color}-light)`,
              border: `1px solid var(--mantine-color-${assessment.color}-3)`,
            }}
          >
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <ThemeIcon color={assessment.color} variant="filled" size="lg" radius="xl">
                {precheck.label === "good" && findings.length === 0
                  ? <IconCheck size={20} />
                  : <IconAlertTriangle size={20} />}
              </ThemeIcon>
              <div>
                <Text fw={700} c={`${assessment.color}.9`}>{assessment.text}</Text>
                {precheck.summary && <Text size="sm" mt={2}>{precheck.summary}</Text>}
              </div>
            </Group>
          </Paper>

          {/* Fields to check — the primary per-field reviewer output. */}
          {findings.length > 0 && (
            <div>
              <Group gap={6} mb={8}>
                <IconClipboardList size={16} />
                <Text size="sm" fw={700}>Fields to check</Text>
                <Badge size="sm" color="orange" variant="light">{findings.length}</Badge>
              </Group>
              <Stack gap={8}>
                {findings.map((f, i) => <FieldFindingRow key={`${f.field}-${i}`} finding={f} />)}
              </Stack>
            </div>
          )}

          {/* Key reasons — case-level drivers, distinct from the per-field list. */}
          {reasons.length > 0 && (
            <>
              {findings.length > 0 && <Divider />}
              <div>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>Key reasons</Text>
                <Stack gap={4}>
                  {reasons.map((r, i) => (
                    <Group key={i} gap={8} wrap="nowrap" align="flex-start">
                      <IconChevronRight size={14} style={{ marginTop: 3, flexShrink: 0 }} />
                      <Text size="sm">{r}</Text>
                    </Group>
                  ))}
                </Stack>
              </div>
            </>
          )}

          {missing.length > 0 && (
            <div>
              <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>Missing fields</Text>
              <Group gap={6}>
                {missing.map((m) => (
                  <Badge key={m} size="sm" color="red" variant="light" style={{ textTransform: "none" }}>
                    {humanizeField(m)}
                  </Badge>
                ))}
              </Group>
            </div>
          )}

          {/* Actual orders / services the customer holds that were reviewed with
              this KYC (not KYC form selections). */}
          {products.length > 0 && (
            <div>
              <Group gap={6} mb={8}>
                <IconShoppingCart size={16} />
                <Text size="sm" fw={700}>Linked orders &amp; services</Text>
                <Badge size="sm" color="blue" variant="light">{products.length}</Badge>
              </Group>
              <Table striped withTableBorder verticalSpacing={6} fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Fund</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Amount / Units</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {products.map((p, i) => (
                    <Table.Tr key={p.id ?? i}>
                      <Table.Td>{productKind(p)}</Table.Td>
                      <Table.Td>{p.fundName ?? "—"}</Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>
                        {typeof p.amount === "number"
                          ? `${p.amount.toLocaleString()} KD`
                          : typeof p.units === "number"
                            ? `${p.units.toLocaleString()} units`
                            : "—"}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}

          <Divider />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">Advisory only — does not change KYC workflow state.</Text>
            {(precheck.modelResponded || precheck.recommendedAction) && (
              <Text size="xs" c="dimmed">
                {precheck.modelResponded ? precheck.modelResponded : ""}
                {precheck.recommendedAction ? ` · ${precheck.recommendedAction.replace(/_/g, " ")}` : ""}
              </Text>
            )}
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
};
